/* eslint-disable eqeqeq */
// React and React Router
import React, { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";

// Third-party libraries
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

// Material-UI components
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Paper, Autocomplete, Dialog, Button, ButtonGroup, Grid, IconButton } from "@mui/material";

// Material-UI icons
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

// Internal services and utilities
import { getInvoiceForm, getCurrency, createOtherInv, replaceOtherInv } from "../../../../services/api";
import { sumOfField, round, numberToStringVN, convertToMoneyWordEN } from "../../../../services/libService";
import { setHeaderTitle } from "../../../../store/layoutSlide";

// Internal components
import { FilterBy } from "../../../commons/filter.component";
import ButtonWithAPICall from "../../../commons/ButtonWithAPICall.component";
import CustomDatePicker from "../../../commons/CustomDatePicker";
import InvoicePreviewOther from "../invOther/invPrevOther";

const initialInputData = {
  form: null,
  customer_name: null,
  customer_pin: null,
  note: null,
  currency: null,
  valid_date: moment(Date.now()).format("YYYY-MM-DD"),
  vat_rate: 0,
  vat: 0,
  invoiceproducts: [
    {
      product_name: "",
      product_unit: "",
      price_no: "",
      quantity: "",
      amount: "",
    },
  ],
};

const vat_type = [
  { id: 1, label: "Không chịu thuế", value: -1 },
  { id: 2, label: "Thuế 0%", value: 0 },
  { id: 3, label: "Thuế 5%", value: 5 },
  { id: 4, label: "Thuế 8%", value: 8 },
  { id: 5, label: "Thuế 10%", value: 10 },
];

// Email validation function
const validateEmail = (email) => {
  if (!email) return true; // Allow empty email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

function CreateOtherInvoice() {
  //boiler plate
  const [t] = useTranslation();
  const productRef = useRef();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  //state
  const [inputData, setInputData] = useState(initialInputData);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailError, setEmailError] = useState(false);

  // Memoized calculations
  const sub_amount = useMemo(() => {
    return sumOfField(inputData.invoiceproducts, "amount") || 0;
  }, [inputData.invoiceproducts]);

  const total = useMemo(() => {
    return sub_amount + (inputData.vat || 0);
  }, [sub_amount, inputData.vat]);

  const inwords = useMemo(() => {
    // Add number to words conversion logic here
    return inputData.currency?.code === "VND" ? numberToStringVN(total) : convertToMoneyWordEN(total);
  }, [total, inputData.currency]);

  const handleChange = useCallback((i, name, value) => {
    setInputData((prev) => {
      const updatedProducts = [...prev.invoiceproducts];

      if (name === "price_no") {
        const quantity = updatedProducts[i].quantity || 0;
        const priceValue = value === "" ? 0 : Number(value.replace(/[^\d.-]/g, ""));
        updatedProducts[i] = {
          ...updatedProducts[i],
          [name]: value,
          amount: priceValue * (Number(quantity) || 0),
        };
      } else if (name === "quantity") {
        const price = updatedProducts[i].price_no || 0;
        const quantityValue = value === "" ? 0 : Number(value.replace(/[^\d.-]/g, ""));
        updatedProducts[i] = {
          ...updatedProducts[i],
          [name]: value,
          amount: (Number(price) || 0) * quantityValue,
        };
      } else {
        updatedProducts[i] = { ...updatedProducts[i], [name]: value };
      }

      return { ...prev, invoiceproducts: updatedProducts };
    });
  }, []);

  const handleRemoveProductLine = useCallback((id) => {
    setInputData((prev) => ({
      ...prev,
      invoiceproducts: prev.invoiceproducts.filter((_, index) => index !== id),
    }));
  }, []);

  const handleAddProductLine = useCallback(() => {
    setInputData((prev) => ({
      ...prev,
      invoiceproducts: [
        ...prev.invoiceproducts,
        {
          product_name: "",
          product_unit: "",
          price_no: "",
          quantity: "",
          amount: "",
        },
      ],
    }));
  }, []);

  const handleVatChange = useCallback(
    (newValue) => {
      setInputData((prev) => ({
        ...prev,
        vat_rate: newValue.value,
        vat: newValue.value !== -1 ? round((sub_amount * newValue.value) / 100, prev.currency?.code === "USD" ? 2 : 0) : 0,
      }));
    },
    [sub_amount]
  );

  const dataForPreview = useMemo(() => {
    return {
      serial: inputData.form,
      valid_date: inputData.valid_date,
      vat_rate: inputData.vat_rate,
      currency: inputData.currency,
      customer_name: inputData.customer_name,
      customer_pin: inputData.customer_pin,
      sub_amount: sub_amount,
      vat: inputData.vat,
      total: total,
      inwords: inwords,
      invoiceproducts: inputData.invoiceproducts,
    };
  }, [inputData, sub_amount, total, inwords]);

  //method

  const handleSubmitResponse = (res) => {
    if (res.code === 200) history.push(`/invoices/${res.data?.id}?type=NAPR`);
  };

  const handleSubmit = useCallback(() => {
    let object = {
      formno: inputData.formno,
      serial: inputData.serial,
      customer_pin: inputData.customer_pin,
      customer_name: inputData.customer_name,
      note: inputData.note,
      currency: inputData.currency?.code,
      valid_date: inputData.valid_date,
      vat_rate: inputData.vat_rate,
      vat: inputData.vat,
      sub_amount: sub_amount,
      total: total,
      invoiceproducts: inputData.invoiceproducts,
      inwords: inwords,
    };
    if (location.state) return replaceOtherInv(location.state.id, object);
    return createOtherInv(object);
  }, [
    inputData.formno,
    inputData.serial,
    inputData.customer_pin,
    inputData.customer_name,
    inputData.note,
    inputData.currency?.code,
    inputData.valid_date,
    inputData.vat_rate,
    inputData.vat,
    inputData.invoiceproducts,
    sub_amount,
    total,
    inwords,
    location.state,
  ]);

  // effect
  useEffect(() => {
    productRef.current = inputData.invoiceproducts;
  }, [inputData.invoiceproducts]);

  useLayoutEffect(() => {
    if (!location.state) return;
    dispatch(setHeaderTitle(location.state ? "PHÁT HÀNH THAY THẾ HOÁ ĐƠN KHÁC" : "TẠO HOÁ ĐƠN KHÁC"));

    // Fetch currency object and form object if needed
    const fetchDataAndSetState = async () => {
      try {
        let currencyObject = location.state.currency;
        let formObject = location.state.form;

        // If currency is a string (currency code), fetch the currency object
        if (typeof location.state.currency === "string") {
          const currencyResponse = await getCurrency({ status: 1 });
          if (currencyResponse.code === 200) {
            currencyObject = currencyResponse.data.find((curr) => curr.code === location.state.currency);
          }
        }

        // If formno and serial are provided, fetch the form object
        if (location.state.form_no && location.state.serial) {
          const formResponse = await getInvoiceForm({ status: 1 });
          if (formResponse.code === 200) {
            formObject = formResponse.data.find((form) => form.form === location.state.form_no && form.series === location.state.serial);
          }
        }

        setInputData((prev) => ({
          ...prev,
          valid_date: moment(location.state.valid_date).format("YYYY-MM-DD"),
          currency: currencyObject,
          form: formObject,
          formno: formObject?.form || location.state.formno,
          serial: formObject?.series || location.state.serial,
          customer_name: location.state.customer_name,
          customer_pin: location.state.customer_tax,
          note: location.state.note,
          invoiceproducts: location.state.invoiceproducts,
          vat_rate: location.state.vat_rate,
          vat: location.state.vat,
          sub_amount: location.state.sub_amount,
          total: location.state.total,
          inwords: location.state.inwords,
        }));
      } catch (error) {
        // Don't set currency or form if API call fails
        setInputData((prev) => ({
          ...prev,
          valid_date: moment(location.state.valid_date).format("YYYY-MM-DD"),
          customer_name: location.state.customer_name,
          customer_pin: location.state.customer_tax,
          note: location.state.note,
          invoiceproducts: location.state.invoiceproducts,
          vat_rate: location.state.vat_rate,
          vat: location.state.vat,
          sub_amount: location.state.sub_amount,
          total: location.state.total,
          inwords: location.state.inwords,
        }));
      }
    };

    fetchDataAndSetState();
  }, [dispatch, location]);

  const formatCurrency = useCallback((amount, currency) => {
    const numAmount = Number(amount) || 0;
    if (currency === "VND") {
      return Math.round(numAmount).toLocaleString("vi-VN");
    }
    return numAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  return (
    <Paper sx={{ padding: 3 }}>
      <Grid container spacing={2}>
        <Grid item sm={2}>
          <FilterBy
            label={t("Mẫu số")}
            getDataFunc={() => getInvoiceForm({ status: 1 })}
            value={inputData.form}
            getOptionLabel={(option) => `${option.form} - ${option.series}` || ""}
            onChange={(e, newValue) =>
              setInputData((prev) => ({
                ...prev,
                form: newValue,
                formno: newValue?.form || null,
                serial: newValue?.series || null,
              }))
            }
          />
        </Grid>
        <Grid item sm={2}>
          <CustomDatePicker
            format="DD/MM/YYYY"
            label={t("Ngày xuất hóa đơn")}
            value={inputData.valid_date}
            onChange={(value) => setInputData((prev) => ({ ...prev, valid_date: value, saleorder: null }))}
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item sm={2}>
          <FilterBy
            label={t("Đơn vị tiền tệ")}
            getDataFunc={() => getCurrency({ status: 1 })}
            getOptionLabel={(option) => option?.code}
            value={inputData.currency}
            onChange={(e, newValue) => setInputData((prev) => ({ ...prev, currency: newValue }))}
          />
        </Grid>
        <Grid item sm={4}>
          <TextField
            fullWidth
            label={t("Tên người mua hàng")}
            value={inputData.customer_name || ""}
            onChange={(e) =>
              setInputData((prev) => ({
                ...prev,
                customer_name: e.target.value,
              }))
            }
          />
        </Grid>
        <Grid item sm={2}>
          <TextField
            label="Số CCCD"
            fullWidth
            value={inputData.customer_pin || ""}
            onChange={(e) =>
              setInputData((prev) => ({
                ...prev,
                customer_pin: e.target.value,
              }))
            }
          />
        </Grid>
        <Grid item sm={4}>
          <TextField
            fullWidth
            label={t("Email người mua hàng")}
            value={inputData.note || ""}
            onChange={(e) => {
              setInputData((prev) => ({
                ...prev,
                note: e.target.value,
              }));
              setEmailError(!validateEmail(e.target.value));
            }}
            error={inputData.note && !validateEmail(inputData.note)}
            helperText={inputData.note && !validateEmail(inputData.note) ? "Email không hợp lệ" : ""}
          />
        </Grid>
      </Grid>

      <div>
        <div style={{ padding: 10, fontSize: 17, backgroundColor: "#f0f3f5", marginTop: 20, textTransform: "uppercase" }}>{"Thông tin hàng hóa"}</div>
        <TableContainer style={{ marginTop: 2 }}>
          <Table>
            <TableHead>
              <TableCell />
              <TableCell style={{ border: "0.5px solid lightgray", width: "5%" }} align="center">
                STT
              </TableCell>
              <TableCell style={{ border: "0.5px solid lightgray", width: "30%" }} align="center">
                Tên hàng hoá, dịch vụ
              </TableCell>
              <TableCell style={{ border: "0.5px solid lightgray", width: "10%" }} align="center">
                Đơn vị tính
              </TableCell>
              <TableCell style={{ border: "0.5px solid lightgray", width: "10%" }} align="center">
                Số lượng
              </TableCell>
              <TableCell style={{ border: "0.5px solid lightgray", width: "10%" }} align="center">
                Tiền tệ
              </TableCell>
              <TableCell style={{ border: "0.5px solid lightgray", width: "15%" }} align="center">
                Đơn giá
              </TableCell>
              <TableCell style={{ border: "0.5px solid lightgray", width: "20%" }} align="center">
                Thành tiền
              </TableCell>
            </TableHead>
            <TableBody>
              {inputData?.invoiceproducts?.map((item, id) => (
                <TableRow key={id}>
                  <TableCell style={{ border: "0.5px solid lightgray", width: "5%" }}>
                    {id === 0 ? (
                      <IconButton color="primary" onClick={handleAddProductLine}>
                        <AddCircleIcon />
                      </IconButton>
                    ) : (
                      <IconButton color="error" onClick={() => handleRemoveProductLine(id)}>
                        <RemoveCircleIcon />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell style={{ border: "0.5px solid lightgray", width: "5%" }}>{id + 1}</TableCell>
                  <TableCell style={{ border: "0.5px solid lightgray", width: "30%" }}>
                    <TextField fullWidth value={item.product_name || ""} onChange={(e) => handleChange(id, "product_name", e.target.value)} />
                  </TableCell>
                  <TableCell style={{ border: "0.5px solid lightgray", width: "10%" }}>
                    <TextField fullWidth value={item.product_unit || ""} inputProps={{ style: { textAlign: "center" } }} onChange={(e) => handleChange(id, "product_unit", e.target.value)} />
                  </TableCell>
                  <TableCell style={{ border: "0.5px solid lightgray", width: "10%" }}>
                    <TextField fullWidth inputProps={{ style: { textAlign: "right" } }} value={item.quantity || ""} onChange={(e) => handleChange(id, "quantity", e.target.value)} />
                  </TableCell>
                  <TableCell style={{ border: "0.5px solid lightgray", width: "10%" }}>
                    <TextField disabled inputProps={{ style: { textAlign: "center" } }} fullWidth value={inputData.currency?.code || ""} />
                  </TableCell>
                  <TableCell style={{ border: "0.5px solid lightgray", width: "15%" }}>
                    <TextField fullWidth inputProps={{ style: { textAlign: "right" } }} value={item.price_no || ""} onChange={(e) => handleChange(id, "price_no", e.target.value)} />
                  </TableCell>
                  <TableCell style={{ border: "0.5px solid lightgray", width: "20%" }}>{formatCurrency(item.amount, inputData.currency?.code)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TableContainer style={{ marginTop: 2, width: "80%" }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell style={{ width: "20%" }} align="left">
                  Cộng tiền hàng
                </TableCell>
                <TableCell style={{ width: "20%" }} align="left" />
                <TableCell style={{ width: "10%" }} align="left">
                  {inputData.currency?.code || ""}
                </TableCell>
                <TableCell style={{ width: "30%" }} align="right">
                  {formatCurrency(sub_amount, inputData.currency?.code)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ width: "20%" }} align="left">
                  Thuế suất GTGT
                </TableCell>
                <TableCell style={{ width: "30%" }} align="center">
                  <Autocomplete
                    disableClearable
                    options={vat_type}
                    getOptionLabel={(option) => option.label}
                    value={vat_type.find((item) => item.value === inputData.vat_rate) || vat_type[1]}
                    onChange={(e, newValue) => handleVatChange(newValue)}
                    renderInput={(params) => <TextField {...params} label={"Thuế GTGT"} />}
                  />
                </TableCell>
                <TableCell style={{ width: "10%" }} align="left">
                  {inputData.currency?.code || ""}
                </TableCell>
                <TableCell style={{ width: "30%" }} align="right">
                  {inputData.vat == -1 ? "\\" : formatCurrency(inputData.vat, inputData.currency?.code)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ width: "20%" }} align="left">
                  Tổng tiền hàng
                </TableCell>
                <TableCell style={{ width: "20%" }} align="left" />
                <TableCell style={{ width: "10%" }} align="left">
                  {inputData.currency?.code || ""}
                </TableCell>
                <TableCell style={{ width: "30%" }} align="right">
                  {formatCurrency(total, inputData.currency?.code)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={4} align="left">
                  Số tiền viết bằng chữ: {inwords}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div align="left">
        <ButtonGroup>
          <Button variant="contained" onClick={() => setDialogOpen(true)}>
            {t("Xem trước")}
          </Button>
          <ButtonWithAPICall api={handleSubmit} response={handleSubmitResponse} variant="contained" color="info">
            {t("Tạo phiếu")}
          </ButtonWithAPICall>
        </ButtonGroup>
      </div>
      <Dialog maxWidth={false} fullWidth={true} scroll="body" onClose={() => setDialogOpen(false)} open={dialogOpen}>
        <InvoicePreviewOther data={dataForPreview} />
      </Dialog>
    </Paper>
  );
}
export default CreateOtherInvoice;
